import { ENGAGEMENTS } from './signing.js';
/**
 * The wording a contract of employment starts as, with gaps where answers go.
 *
 * ## Why the letter is a template and not a form
 *
 * A form asks fifteen questions and prints a table of answers. A letter of
 * appointment says "you will be paid a monthly gross salary of KES 20,000 on
 * day 2 of each month", and that sentence is the thing being agreed to.
 * Somebody signing should be reading their own terms inside the paragraph that
 * binds them, not cross-referencing a grid.
 *
 * So the body carries `{{key}}` wherever a value belongs. The keys are exactly
 * the keys in `employmentFields`, and a test asserts that in both directions —
 * a placeholder naming a field nobody collects prints as a blank rule for
 * ever, and a required field appearing nowhere in the letter is a question
 * asked for no reason.
 *
 * ## Why the text is copied onto each contract rather than referenced
 *
 * This is the wording a contract is *raised* with. It is stored on that
 * contract and hashed into both signatures, so editing this template cannot
 * reach an agreement somebody has already signed. One living template that
 * every contract pointed at would mean a change to clause 9 silently changing
 * what eighty people agreed to — the specific thing a signature exists to
 * prevent.
 *
 * The wording follows the appointment letter this business already issues on
 * paper, so the signed copy and the filed copy say the same things in the same
 * order.
 */
export function employmentLetter(kind) {
    const engagement = ENGAGEMENTS[kind];
    const label = engagement.label.toLowerCase();
    /*
     * A trial period only means something against a term long enough to have
     * one. Three months' probation inside a three-month attachment is the whole
     * engagement, so an attachment and an internship are evaluated at the end
     * instead of part-way through.
     */
    const term = kind === 'employment'
        ? `This appointment is for an initial term of ${engagement.months} months from the effective start date, the first three of which are a trial period. It will be reviewed, and may be renewed, on the evaluation carried out at the end of that trial period.`
        : `This ${label} runs for ${engagement.months} months from the effective start date. It will be reviewed at the end of that period, and may be extended or converted to employment on the evaluation carried out then.`;
    const opening = kind === 'employment'
        ? 'We are pleased to offer you employment in the position of **{{position}}**.'
        : `We are pleased to offer you a ${label} with us in the position of **{{position}}**.`;
    const counterpartyHeading = kind === 'employment' ? 'EMPLOYEE' : engagement.label.toUpperCase();
    return [
        '# PERSONAL & CONFIDENTIAL',
        '',
        `**${engagement.label.toUpperCase()} — LETTER OF APPOINTMENT**`,
        '',
        '| | |',
        '|---|---|',
        '| Name | {{employeeName}} |',
        '| National ID no. | {{nationalId}} |',
        '| KRA PIN | {{kraPin}} |',
        '| Mobile telephone no. | {{mobile}} |',
        '| Email | {{employeeEmail}} |',
        '',
        'Dear {{employeeName}},',
        '',
        opening,
        '',
        'Always ensure your own safety before anything else, and wear the protective equipment required in the field.',
        '',
        'The terms and conditions are as follows.',
        '',
        '## 1. Effective start date and probation',
        '',
        `${term} The effective start date for this role is **{{startDate}}**.`,
        '',
        ...(kind === 'employment'
            ? [
                /*
                 * Probation was described only as "a trial period" inside the term
                 * sentence, which is not a probationary contract — and section 42 of
                 * the Employment Act gives probation its own rules. Stated properly
                 * it protects both sides: seven days either way while everybody finds
                 * out whether this works.
                 */
                'The first three months are a probationary period under section 42 of the Employment Act, 2007. During probation either party may end this contract by giving seven days&rsquo; notice in writing, or seven days&rsquo; basic pay in lieu of notice. Probation may be extended once, by a further period of up to three months, with your written agreement.',
                '',
            ]
            : []),
        '## 2. Reporting',
        '',
        'You will be based at **{{basedAt}}** and will report to **{{reportsTo}}**.',
        '',
        '## 3. Duties and responsibilities',
        '',
        'You will be responsible for the maintenance and repair of the network in the field in the area you are assigned to, and for ensuring that every installation meets the standards required in Kenya. Where you cannot resolve an outage yourself, report it to the person named above.',
        '',
        '- Administrative work on sites, and management of the locations you are assigned.',
        '- Ensuring the sites run correctly each day, and that ground operations run smoothly.',
        '- Site documentation and the keeping of office files.',
        '- Filing a site report twice a month through this system.',
        '- Calling the site engineer for your location in the event of an outage you cannot resolve.',
        '- Recording your attendance in this system, so that time spent on site installations is accounted for.',
        '',
        "- You will observe the Company's rules and regulations and diligently discharge the duties assigned to you.",
        '- You will not, without the express approval of the Company, disclose any of its confidential matters to anyone not authorised to receive them, nor engage in any business or occupation that conflicts with the interests of the Company.',
        '',
        '## 4. Training and certification',
        '',
        /*
         * This said "certification in two of the products the Company deals in
         * every quarter", named no products, said nothing about who pays, and tied
         * the whole thing to separation. An obligation somebody can fail through no
         * fault of their own should not be a ground for dismissal.
         */
        'The Company will arrange and pay for training and certification relevant to your role. You are expected to complete the courses assigned to you and to keep any certification your work requires — including the pole-climbing certification — valid and current. Where the Company has not made a course available, you are not in breach of this clause.',
        '',
        '## 5. Performance',
        '',
        'Your performance will be reviewed against the work recorded in this system: jobs completed, jobs finished within the time promised to the customer, repeat visits, and customer feedback. Reviews are held monthly, and at the end of the probationary period.',
        '',
        '## 6. Other engagements',
        '',
        'You will not engage in any other business that is in direct conflict with the business of the Company.',
        '',
        '## 7. Remuneration',
        '',
        'In consideration of the services to be rendered by you under this contract, you will be paid a monthly gross salary of **KES {{grossMonthlyKes}}**, on day **{{payDayOfMonth}}** of each month, on submission of your monthly attendance record.',
        '',
        /*
         * Named rather than left to the payslip. These are deducted whatever the
         * contract says, and a person who has only ever been told a gross figure
         * reads their first net payment as a mistake.
         */
        'Your salary is stated gross. The Company will deduct from it, and remit, the amounts required by law — PAYE, the National Social Security Fund, the Social Health Insurance Fund and the Affordable Housing Levy — at the rates in force from time to time. Your payslip will show each deduction separately.',
        '',
        'The Company will reimburse all reasonable and necessary expenses incurred because of the services rendered. Proof of payment and receipts must be included with every claim.',
        '',
        ...(kind === 'employment'
            ? [
                '## 8. Field allowances',
                '',
                /*
                 * The whole reason this rewrite happened: the letter set out a salary
                 * and said nothing about the per-job rates this business actually
                 * pays. Paid outside the contract they are a discretionary bonus that
                 * can be stopped, which is the opposite of what both sides intend and
                 * the sort of thing that is argued about at separation.
                 */
                'In addition to your salary, the Company pays the following allowances for field work you complete and record in this system. They are paid with the salary for the month in which the work was verified.',
                '',
                '| Work | Allowance | Limit |',
                '|---|---|---|',
                '| New customer connection | KES {{ratePerConnectionKes}} each | up to {{connectionsPerDayCap}} per day |',
                '| Cable run on poles | KES {{ratePerKilometreKes}} per kilometre | measured and verified on site |',
                '| Successful FAT configuration | KES {{ratePerFatKes}} each | verified by the site engineer |',
                '',
                'An allowance is earned when the work is recorded in this system and verified. Work that has to be redone because it was not done correctly the first time is one piece of work, not two. The Company may change these rates by giving you one month&rsquo;s written notice; a change does not affect work already completed.',
                '',
                '## 9. Leave',
            ]
            : ['## 8. Leave']),
        '',
        /*
         * Silent before. The statutory minimums apply whether or not a contract
         * mentions them, so the only thing silence achieved was making the document
         * look as though nobody had considered them.
         */
        'You are entitled to 21 working days of paid annual leave for each 12 consecutive months of service, under section 28 of the Employment Act, 2007. Leave is taken at times agreed with the person you report to, so that cover in your area is maintained.',
        '',
        'After two consecutive months of service you are entitled to sick leave of seven days with full pay, and thereafter seven days with half pay, in each period of twelve consecutive months, on production of a certificate from a qualified medical practitioner. Maternity and paternity leave are as provided by section 29 of the Act.',
        '',
        `## ${kind === 'employment' ? '10' : '9'}. Company property`,
        '',
        /*
         * A technician holds an OTDR, a handset, a SIM that can spend money, and a
         * set of keys. The old letter mentioned returning property only inside the
         * secrecy declaration, which is the wrong place and easily missed.
         */
        'Tools, test equipment, handsets, SIM cards, keys, uniforms and any other property issued to you remain the property of the Company. You will take reasonable care of them, use them only for the work, and return all of them on the day your engagement ends or earlier on request. The Company may recover the cost of property lost through your wilful act or gross negligence, subject to section 19 of the Employment Act, 2007.',
        '',
        `## ${kind === 'employment' ? '11' : '10'}. Termination`,
        '',
        /*
         * The old wording opened "Your engagement with the Company is at will",
         * gave the Company two weeks and the employee two months, and listed
         * "inefficiency" as grounds for dismissal without notice.
         *
         * At-will employment does not exist in Kenya: sections 41, 43 and 45
         * require a valid reason and a fair hearing. Section 35 sets 28 days for
         * monthly-paid staff, and a term less favourable than the Act does not bind
         * the employee — so the asymmetry was unenforceable as well as unfair.
         * Section 44 sets out what justifies summary dismissal, and inefficiency is
         * not among them; a dismissal on that ground would very likely be found
         * unfair.
         */
        'After the probationary period, either party may end this contract by giving 28 days&rsquo; notice in writing, or payment of 28 days&rsquo; basic salary in lieu of notice, in accordance with section 35 of the Employment Act, 2007.',
        '',
        'Before terminating your employment for reasons relating to your conduct or performance, the Company will explain the reason to you and give you an opportunity to respond, with a colleague or representative present if you wish, as required by section 41 of the Act.',
        '',
        'The Company may dismiss you summarily, without notice, only on the grounds set out in section 44 of the Act — which include gross misconduct, theft, and conduct that fundamentally breaches your obligations under this contract.',
        '',
        `## ${kind === 'employment' ? '12' : '11'}. Indemnity`,
        '',
        /*
         * "To the full extent for any loss or damage resulting from any breach"
         * made a technician personally liable for a cut backbone. Unlimited
         * employee liability for ordinary mistakes is not something a Kenyan court
         * is likely to enforce, and a clause nobody will enforce is worse than a
         * narrower one that holds.
         */
        'You will indemnify the Company against loss or damage caused by your wilful misconduct, gross negligence, or fraud. This clause does not make you liable for loss arising from honest mistakes made in the ordinary course of your work.',
        '',
        "You are required to read, understand and comply with the Company's code of conduct and the other rules and regulations of service applicable to your engagement. All other standards, practices and policies of the Company as they exist now, and as they may be amended from time to time, apply to you and you are expected to abide by them.",
        '',
        `## ${kind === 'employment' ? '13' : '12'}. Confidentiality and personal data`,
        '',
        'You undertake and declare that you will observe the strictest professional discretion, and will not divulge to any party whatsoever — at any time during your period of service with the Company or after having left it — any information concerning the transactions of the Company or its customers, except upon the express instructions of the Company. In the absence of proper instructions you will not allow any person access to books, letters or papers belonging to the Company or relating to its business, nor furnish extracts from them, nor give any information concerning the business affairs of the Company or its customers to any person not lawfully entitled to it. All inventions, improvements and discoveries made by you, alone or with others, become the sole property of the Company.',
        '',
        /*
         * Named because the work involves customer names, addresses and phone
         * numbers on a handset in the field, and the Act imposes duties on the
         * person handling them and not only on the business.
         */
        'In the course of your work you will handle personal data belonging to customers and colleagues. You will use it only for the work, keep it on Company systems rather than your own, and comply with the Data Protection Act, 2019 and the policies the Company issues under it.',
        '',
        'You are expected to observe confidentiality and to comply with the security and confidential matters set out in the declaration of secrecy below, which is signed separately.',
        '',
        `## ${kind === 'employment' ? '14' : '13'}. Information accuracy`,
        '',
        'This offer is based on the information given in your application for this role. If at any time it comes to the knowledge of management that any of that information is incorrect, or that relevant information was withheld, the engagement based on this letter is liable to be terminated in accordance with the Act.',
        '',
        `## ${kind === 'employment' ? '15' : '14'}. Governing law and disputes`,
        '',
        'This agreement is governed by, and construed in accordance with, the laws of Kenya. Any dispute that cannot be settled between the parties may be referred to the Employment and Labour Relations Court.',
        '',
        `## ${kind === 'employment' ? '16' : '15'}. Entire agreement`,
        '',
        'This agreement constitutes the entire agreement between the parties, and supersedes all prior agreements, understandings and representations. Nothing in it reduces any entitlement given to you by the Employment Act, 2007 or any other law.',
        '',
        'The parties agree to the terms and conditions set out above, as demonstrated by their signatures below.',
        '',
        '---',
        '',
        '**FOR THE COMPANY**',
        '',
        '| | |',
        '|---|---|',
        '| Name | {{companySignatoryName}} |',
        '| Title | {{companySignatoryTitle}} |',
        '| Signed | {{companySignature}} |',
        '',
        `**THE ${counterpartyHeading}**`,
        '',
        '| | |',
        '|---|---|',
        '| Name | {{employeeName}} |',
        '| Title | {{position}} |',
        '| Signed | {{employeeSignature}} |',
        '',
        '---',
        '',
        '# DECLARATION OF SECRECY',
        '',
        'I, **{{employeeName}}**, national ID no. **{{nationalId}}**, contracted by the Company, solemnly declare that:',
        '',
        'I will observe strict discretion, and shall not at any time disclose — directly or indirectly, to any other person whatsoever, including to the public or any section of the public — any information concerning this agreement or any other matter regarding the internal affairs of the Company or any of its associated companies, whether or not that information is stated to be confidential, without express written permission.',
        '',
        'I further solemnly declare that I will not disclose any trade secret or other information of a confidential nature relating to the Company, or to any of its associated companies or their business, or in respect of which the Company owes an obligation of confidence to a third party, during or after my engagement, except in the proper course of my engagement or as required by law.',
        '',
        'I further state that I will safeguard the assets and property of the Company and of its clients entrusted to me, and that I must return to the Company or client upon request — and in any event upon the termination of my engagement — all documents and tangible items which belong to the Company or client, or which contain or refer to any confidential information, and which are in my possession or under my control.',
        '',
        'I understand that the contractual relationship between the Company and the people who work for it is founded on trust, and that any breach of that trust by me, such as the unauthorised disclosure to a third party of confidential information about matters connected with its business, will render me liable to disciplinary action.',
        '',
        '| | |',
        '|---|---|',
        '| Signed | {{secrecySignature}} |',
        '| Name | {{employeeName}} |',
        '| Designation | {{position}} |',
        '',
    ].join('\n');
}
/** Every `{{key}}` the letter asks for, once each, in the order they appear. */
export function letterPlaceholders(body) {
    const seen = new Set();
    for (const match of body.matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g)) {
        if (match[1])
            seen.add(match[1]);
    }
    return [...seen];
}
/**
 * Put the answers into the words.
 *
 * A key with nothing against it becomes a rule to write on rather than
 * vanishing, because a contract printed before it is finished should look
 * unfinished — an empty string would print a sentence that reads as complete
 * and says nothing.
 */
/**
 * Which `{{placeholders}}` a document actually contains.
 *
 * ## Why anything needs to ask
 *
 * Not every field is a gap in the text. Most are — the property name, the
 * notice period, the signatory — and those can be typed into the sentence that
 * asks for them. A few are not: "Installation fee" is a choice between free, a
 * one-time fee and another arrangement, and what the agreement carries is the
 * *sentence that choice produces*, `{{feeDescription}}`. There is nowhere in
 * the text to put a dropdown, because the text does not contain the question.
 *
 * Six of the landlord agreement's fields are like that, and moving the filling
 * into the document without noticing took away the only way to set any of
 * them — including the exclusivity clause, which is the one a landlord is most
 * likely to be held to years later.
 *
 * So a screen asks the document what it contains, fills those gaps in place,
 * and keeps a short form for the rest. The split is derived from the wording
 * rather than listed by hand, which matters because the wording is editable:
 * somebody who rewrites a clause to mention `{{feeAmountKes}}` directly gets an
 * inline gap for it that afternoon, with nothing to update here.
 */
export function placeholdersIn(body) {
    const found = new Set();
    for (const match of body.matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g)) {
        found.add(match[1]);
    }
    return found;
}
export function fillLetter(body, values, 
/**
 * Keys to leave as `{{placeholders}}` rather than filling in.
 *
 * For the screen where somebody is filling the document in. Their own gaps
 * stay open so the page can put an input in each one and they type into the
 * sentence that binds them, rather than into a form underneath it and
 * checking afterwards that the right blank moved.
 *
 * Everything not named here is filled exactly as before, so a document
 * rendered for reading, printing or hashing is unaffected — which matters,
 * because the printed copy and the fingerprint must not know this feature
 * exists.
 */
leaveOpen = new Set()) {
    return body.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (whole, key) => {
        if (leaveOpen.has(key))
            return whole;
        const value = values[key];
        return value === undefined || value === null || String(value).trim() === ''
            ? '__________'
            : String(value);
    });
}
//# sourceMappingURL=employment-letter.js.map